import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Phone, Mail, MessageCircle, Building2, Clock, ShieldCheck,
  ArrowUpRight, Copy,
} from "lucide-react";
import { toast } from "sonner";

const SUPPORT = {
  company: "SINDHE COMMUNICATION",
  phone: "+919640945857",
  phoneDisplay: "+91 96409 45857",
  email: "maaprintbharath@gmail.com",
  whatsappText:
    "Hi MaaPrint Support, I need help with my account.",
};

const FAQS = [
  {
    q: "How does auto-print work?",
    a: "When a walk-in customer pays through your shop QR, the file lands in your Orders queue and the MaaPrint Windows Connector prints it silently on the default printer — no popups, no clicks.",
  },
  {
    q: "Why is my subscription required?",
    a: "An active subscription keeps your shop QR live for walk-in customers. Without it, guests cannot pay or send prints to your queue.",
  },
  {
    q: "When does my wallet get credited?",
    a: "Immediately after Razorpay confirms the customer's payment. Retailer gets 96% and MaaPrint retains a 4% commission. Both credits show in your transaction history.",
  },
  {
    q: "How do I withdraw earnings?",
    a: "Open Wallet → Withdraw to bank. Submit bank/UPI details and MaaPrint admin will approve and payout to your account.",
  },
];

export default function Help() {
  const copy = (v) =>
    navigator.clipboard.writeText(v).then(() => toast.success(`Copied ${v}`));

  const waLink =
    `https://wa.me/${SUPPORT.phone.replace(/[^0-9]/g, "")}` +
    `?text=${encodeURIComponent(SUPPORT.whatsappText)}`;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="help-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">
          Help center
        </p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">
          Help &amp; Support
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Our team responds within a few working hours. Choose the channel that suits you.
        </p>
      </div>

      {/* Contact channels grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Phone */}
        <Card className="p-5 border-border bg-card hover:shadow-md transition-shadow" data-testid="help-phone-card">
          <div className="h-11 w-11 rounded-lg bg-brand-blue/10 text-brand-blue flex items-center justify-center mb-3">
            <Phone size={20} />
          </div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Call us</p>
          <a
            href={`tel:${SUPPORT.phone}`}
            className="mt-1 text-lg font-display font-semibold text-foreground hover:text-brand-blue transition-colors block"
            data-testid="help-phone-link"
          >
            {SUPPORT.phoneDisplay}
          </a>
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
            <Clock size={12} /> Mon-Sat · 9:00 - 20:00 IST
          </p>
          <div className="mt-4 flex gap-2">
            <Button asChild size="sm" className="flex-1 bg-brand-blue hover:bg-brand-blue-hover">
              <a href={`tel:${SUPPORT.phone}`} data-testid="help-phone-cta">
                <Phone size={13} className="mr-1.5" /> Call now
              </a>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => copy(SUPPORT.phoneDisplay)}
              data-testid="help-phone-copy"
            >
              <Copy size={13} />
            </Button>
          </div>
        </Card>

        {/* Email */}
        <Card className="p-5 border-border bg-card hover:shadow-md transition-shadow" data-testid="help-email-card">
          <div className="h-11 w-11 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center mb-3">
            <Mail size={20} />
          </div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Email us</p>
          <a
            href={`mailto:${SUPPORT.email}?subject=MaaPrint%20Support%20Request`}
            className="mt-1 text-lg font-display font-semibold text-foreground hover:text-brand-green transition-colors block break-all"
            data-testid="help-email-link"
          >
            {SUPPORT.email}
          </a>
          <p className="text-xs text-muted-foreground mt-2">
            Reply within 24h · Attach screenshots if any
          </p>
          <div className="mt-4 flex gap-2">
            <Button asChild size="sm" className="flex-1 bg-brand-green hover:bg-brand-green-hover text-white">
              <a
                href={`mailto:${SUPPORT.email}?subject=MaaPrint%20Support%20Request`}
                data-testid="help-email-cta"
              >
                <Mail size={13} className="mr-1.5" /> Send email
              </a>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => copy(SUPPORT.email)}
              data-testid="help-email-copy"
            >
              <Copy size={13} />
            </Button>
          </div>
        </Card>

        {/* WhatsApp */}
        <Card className="p-5 border-border bg-card hover:shadow-md transition-shadow" data-testid="help-whatsapp-card">
          <div className="h-11 w-11 rounded-lg bg-[#25D366]/10 text-[#128C7E] flex items-center justify-center mb-3">
            <MessageCircle size={20} />
          </div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">WhatsApp</p>
          <p className="mt-1 text-lg font-display font-semibold">Chat with us</p>
          <p className="text-xs text-muted-foreground mt-2">
            Fastest response · Any language
          </p>
          <div className="mt-4">
            <Button asChild size="sm" className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white">
              <a
                href={waLink}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="help-whatsapp-cta"
              >
                <MessageCircle size={13} className="mr-1.5" />
                Open WhatsApp
                <ArrowUpRight size={12} className="ml-1" />
              </a>
            </Button>
          </div>
        </Card>
      </div>

      {/* Company card */}
      <Card className="p-6 border-border bg-card" data-testid="help-company-card">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Building2 size={22} />
          </div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Company</p>
            <h3 className="mt-1 font-display text-xl font-semibold" data-testid="help-company-name">
              {SUPPORT.company}
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Authorised operator of the MaaPrint Cloud Print Platform for Telangana &amp; India.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Badge variant="outline" className="bg-brand-blue/5 border-brand-blue/20 text-brand-blue">
                <ShieldCheck size={12} className="mr-1" /> Verified merchant
              </Badge>
              <Badge variant="outline" className="bg-brand-green/5 border-brand-green/20 text-brand-green">
                <Clock size={12} className="mr-1" /> Live support Mon–Sat
              </Badge>
            </div>
          </div>
        </div>
      </Card>

      {/* FAQ */}
      <Card className="p-6 border-border bg-card">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">FAQ</p>
        <h3 className="font-display text-xl font-semibold mt-1">Frequently asked questions</h3>
        <div className="mt-4 divide-y divide-border">
          {FAQS.map((f, i) => (
            <div key={i} className="py-4 first:pt-0 last:pb-0" data-testid={`help-faq-${i}`}>
              <p className="font-medium text-sm">{f.q}</p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <Separator className="my-4" />
        <p className="text-xs text-muted-foreground">
          Still stuck? Reach us on WhatsApp — we usually reply within a few minutes during business hours.
        </p>
      </Card>
    </div>
  );
}
